import { CheckCircle, Close, Email, Phone } from "@mui/icons-material";
import { Modal, Skeleton, Stack, Typography, useTheme } from "@mui/material";
import { Box } from "@mui/system";
import React from "react";
import { Link, useHistory } from "react-router-dom";
import { trackEvent } from "src/Amplitude";
import BrandArrowDown from "src/icons/BrandArrowDown";
import CopyToClipboardIcon from "src/icons/CopyToClipboardIcon";
import GuestBrokerSvg from "src/icons/GuestBrokerSvg";
import PhoneCallIcon from "src/icons/PhoneCallIcon";
import Profileicon from "src/icons/Profileicon";
import api from "src/store/features/api";
import { useUserSlice } from "src/store/features/user.slice";
import CopyOnClick from "src/utils/hooks/useCopy";
import usePermission from "src/utils/hooks/usePermission";
import StyledButton from "../dashboard/StyledButton";
import {
  BrokerNameWrapper,
  BrokerProfileCircle,
  BrokerProfileWrapper,
  BrokerText,
  BrokerTextWrapper,
  FlagIconPositioning,
  FlagIconWrapper,
  GuestIconWrapper,
  ImageFrameWrapper,
} from "../multi-account/index.styles";
import {
  BrokerContentWrapper,
  BrokerInfoWrapper,
  BrokerStatusWrapper,
  ContactDetailsWrapper,
  ContactSubtitle,
  ContactText,
  ContactWrapper,
  EmailWrapper,
  IconWrapper,
  ModalWrapper,
  PhoneNumberRow,
  PhoneNumbersWrapper,
  PhoneNumberValues,
  StatusBadge,
  Tooltip,
} from "./styles/brokerHumanisation.styles";
import useVerificationNavigation from "../common/useVerificationNavigation";

const IconBox = (props: { icon: JSX.Element; color?: string }) => {
  return (
    <Box
      sx={{
        border: `2px solid #${props.color ?? "#002035"}`,
        borderRadius: "50px",
        width: 30,
        height: 30,
        textAlign: "center",
        paddingTop: "4px",
      }}>
      <div>{props.icon}</div>
    </Box>
  );
};

const Tile = (props: {
  icon: JSX.Element;
  clickableText?: string;
  title?: string;
  description?: string;
  borderColor?: string;
  countryCode?: string | undefined;
  hideIcon?: boolean;
  status?: "online" | "offline";
}) => {
  const theme = useTheme();
  const { isGuest } = usePermission();
  const PhoneTo = (props: { phoneNumber: string; countryCode?: string }) => {
    return (
      <div style={{ display: "flex" }}>
        {props.countryCode && !isGuest && <span>{props.countryCode}</span>}
        <Link
          style={{ color: theme.palette.text.primary }}
          to="#"
          onClick={(e) => {
            trackEvent(
              `Clicks_Broker_PhoneNumber`,
              {
                Broker_PhoneNumber: props.phoneNumber,
              },
              "Broker_Humanisation",
            );
            window.location.href = "tel:" + props.phoneNumber;
            e.preventDefault();
          }}>
          <span style={{ marginLeft: "6px" }}>{props.phoneNumber}</span>
        </Link>
      </div>
    );
  };

  return (
    <>
      <Stack direction={"row"}>
        {props.status !== "offline" && (
          <div style={{ display: props.hideIcon ? "none" : "block" }}>
            <IconBox icon={props.icon} color={props.borderColor ?? "0BEC9F"} />
          </div>
        )}
        {props.status === "offline" && (
          <div style={{ display: props.hideIcon ? "none" : "block" }}>
            <IconBox icon={props.icon} color={"ADC0CB"} />
          </div>
        )}
        <Stack ml={2} alignSelf={"center"}>
          {props.clickableText && (
            <>
              {props.clickableText.includes("@") && <CopyOnClick text={props.clickableText} />}
              {props.clickableText.includes("+1") && (
                <PhoneTo phoneNumber={props.clickableText} countryCode={"INTL"} />
              )}
              {props.clickableText.includes("+61") && (
                <PhoneTo phoneNumber={props.clickableText} countryCode={"AU"} />
              )}
            </>
          )}
          <Typography sx={{ fontSie: "16px", color: theme.palette.text.primary }}>
            {props.title}
          </Typography>
          <Typography sx={{ fontSie: "14px", color: "#7b858b" }}>{props.description}</Typography>
        </Stack>
      </Stack>
    </>
  );
};

const isBrokerOnline = (countryCode: string) => {
  const AuStartTime = "7:00:00";
  const AUEndTime = "17:00:00";
  const UKStartTime = "8:00:00";
  const UKEndTime = "20:00:00";
  const clean = (time: string) => {
    return +time.replace(/:/g, "");
  };
  const AuTimeNow = new Date().toLocaleTimeString("it-IT", {
    timeZone: "Australia/Sydney",
  });
  const UkTimeNow = new Date().toLocaleTimeString("it-IT", {
    timeZone: "Europe/London",
  });
  const isAusBrokerOnline =
    clean(AuTimeNow) > clean(AuStartTime) && clean(AUEndTime) > clean(AuTimeNow);
  const isUkBrokerOnline =
    clean(UkTimeNow) > clean(UKStartTime) && clean(UKEndTime) > clean(UkTimeNow);

  if (countryCode === "UK") return isUkBrokerOnline;
  if (countryCode === "AU") return isAusBrokerOnline;
};

export default function ProfileComponent(props: {
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  hideIcon?: boolean;
  disableClick?: boolean;
  text: "light" | "dark";
}) {
  const { isGuest } = usePermission();
  const theme = useTheme();
  const {
    broker: { countryCode, imgUrl, name },
  } = useUserSlice();

  const textColor = props.text === "light" ? "#FFF" : theme.palette.text.primary;
  const isOnline = isGuest ? false : isBrokerOnline(countryCode || "AU");
  const imgSrc = !isGuest && imgUrl;
  const brokerName = isGuest ? `Broker` : name;
  const brokerLabel = isGuest ? `Unassigned` : `Contact Broker`;

  const handleBrokerClick = () => {
    trackEvent(
      "Dashboard",
      {
        action: "open",
        broker: name,
      },
      "Broker_Details",
    );
    props.setOpen(true);
  };

  return (
    <BrokerProfileWrapper onClick={handleBrokerClick} disabled={!!props.disableClick}>
      <ImageFrameWrapper>
        {/* Broker profile picture */}
        {imgSrc && (
          <>
            <BrokerProfileCircle isOnline={isOnline}>
              <img src={imgSrc} alt="profile" height="33px" />
            </BrokerProfileCircle>
            <FlagIconWrapper>
              <FlagIconPositioning>
                <PhoneCallIcon />
              </FlagIconPositioning>
            </FlagIconWrapper>
          </>
        )}

        <GuestIconWrapper isGuest={isGuest}>
          <Profileicon size="14" />
        </GuestIconWrapper>
      </ImageFrameWrapper>

      <BrokerTextWrapper>
        <BrokerNameWrapper>
          {/* Broker name */}
          {brokerName ? (
            <BrokerText color={textColor} fontSize="14px" lineHeight="17px" bold>
              {brokerName}
            </BrokerText>
          ) : (
            <Skeleton variant="text" width={120} />
          )}

          {/* Down arrow icon */}
          {!props.hideIcon && <BrandArrowDown text={props.text} />}
        </BrokerNameWrapper>

        {/* Broker description */}
        <BrokerText color={textColor} fontSize="12px" lineHeight="14px">
          {brokerLabel}
        </BrokerText>
      </BrokerTextWrapper>
    </BrokerProfileWrapper>
  );
}

interface UserBrokerModalProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  brokerDetails: {
    email: string;
    phoneNumberAU: string;
    phoneNumberUS: string;
    isOnline: boolean;
  };
}
const UserBrokerModal = (props: UserBrokerModalProps) => {
  const { open, setOpen, brokerDetails } = props;
  const { email, phoneNumberAU, phoneNumberUS, isOnline } = brokerDetails;
  const statusColor = isOnline ? "#0BEC9F" : "#FCD34D";
  const statusText = isOnline
    ? "Your Broker is typically online at this time"
    : "Your Broker's team is online";

  const handleOnClose = () => {
    setOpen(false);
  };

  const formatPhoneNumber = (phoneNumber: string) => {
    return phoneNumber.replace(/[-\s]/g, "");
  };

  const [copyAU, setCopyAU] = React.useState<boolean>(false);
  const [copyUS, setCopyUS] = React.useState<boolean>(false);
  const copyToClipboard = (copy: string, type: string) => {
    // end function if false
    if (!window.isSecureContext) {
      return;
    }
    navigator.clipboard.writeText(copy);
    if (type === "AU") {
      setCopyAU(true);
    } else {
      setCopyUS(true);
    }
    setTimeout(() => {
      if (type === "AU") {
        setCopyAU(false);
      } else {
        setCopyUS(false);
      }
    }, 1000);
  };

  return (
    <ModalWrapper open={open} onClose={handleOnClose}>
      {/* Broker name and profile picture */}
      <BrokerInfoWrapper>
        <ProfileComponent hideIcon={true} setOpen={() => false} disableClick={true} text={"dark"} />
      </BrokerInfoWrapper>

      {/* Broker status and contact details */}
      <BrokerContentWrapper>
        {/* Broker status */}
        <BrokerStatusWrapper>
          <StatusBadge color={statusColor} />
          <ContactText>{statusText}</ContactText>
        </BrokerStatusWrapper>

        {/* Broker contact numbers */}
        <ContactWrapper>
          <ContactSubtitle>Contact Details</ContactSubtitle>
          <ContactDetailsWrapper>
            {/* Email */}
            <EmailWrapper>
              <IconWrapper border="0.667px solid  #E5E7E8">
                <Email sx={{ height: "14px", width: "14px" }} />
              </IconWrapper>
              <ContactText>{email}</ContactText>
            </EmailWrapper>

            {/* Phone numbers */}
            <PhoneNumbersWrapper>
              <IconWrapper border="0.667px solid  #E5E7E8">
                <Phone sx={{ height: "14px", width: "14px" }} />
              </IconWrapper>
              <PhoneNumberValues>
                {/* INTL */}
                <PhoneNumberRow>
                  <ContactText>INTL</ContactText>
                  <ContactText isLink style={{ flex: "1 0 0" }}>
                    <OnClickWrapper data={phoneNumberUS}>
                      <>{formatPhoneNumber(phoneNumberUS)}</>
                    </OnClickWrapper>
                  </ContactText>
                  <IconWrapper
                    background="#F8F9F9"
                    style={{ cursor: "pointer" }}
                    onClick={() => copyToClipboard(phoneNumberUS, "US")}>
                    <Tooltip show={copyUS}>copied</Tooltip>
                    <CopyToClipboardIcon size="14px" />
                  </IconWrapper>
                </PhoneNumberRow>

                {/* AU */}
                <PhoneNumberRow>
                  <ContactText style={{ width: "29px" }}>AU</ContactText>
                  <ContactText isLink style={{ flex: "1 0 0" }}>
                    <OnClickWrapper data={phoneNumberAU}>
                      <>{formatPhoneNumber(phoneNumberAU)}</>
                    </OnClickWrapper>
                  </ContactText>
                  <IconWrapper
                    background="#F8F9F9"
                    style={{ cursor: "pointer" }}
                    onClick={() => copyToClipboard(phoneNumberAU, "AU")}>
                    <Tooltip show={copyAU}>copied</Tooltip>
                    <CopyToClipboardIcon size="14px" />
                  </IconWrapper>
                </PhoneNumberRow>
              </PhoneNumberValues>
            </PhoneNumbersWrapper>
          </ContactDetailsWrapper>
        </ContactWrapper>
      </BrokerContentWrapper>
    </ModalWrapper>
  );
};

const GuestBrokerModal = (props: {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const { open, setOpen } = props;
  const history = useHistory();
  const theme = useTheme();
  const { handleVerification } = useVerificationNavigation();

  const guestBrokerDetails = `Make informed decisions on your crypto investments & join the ranks of our KYC-verified clients today to enjoy the exclusive benefits of a personal broker & exciting new portal features! By verifying your account, you’re on board to receive:`;
  const benefitLines = [
    "Latest market news & updates from expert brokers",
    "Unparalled 24/7 personalised support",
    "Step-by-step guidance on your investment journey",
    "Seamless transactions & secure verification processes handled by your personal broker",
  ];

  return (
    <Modal open={open} onClose={() => setOpen(false)}>
      <Box
        sx={{
          position: "absolute" as "absolute",
          top: "45%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          minWidth: "416px",
          borderRadius: "10px",
          bgcolor: "background.paper",
          boxShadow: 24,
          padding: "16px",
          [theme.breakpoints.down("md")]: {
            width: 350,
          },
        }}>
        <Stack direction={"row"} justifyContent={"space-between"} sx={{ padding: "10px" }}>
          <Typography
            sx={{
              fontSize: "18px",
              fontWeight: "600",
              color: theme.palette.primary.main,
            }}>
            Verify account to be assigned a broker
          </Typography>
          <Close onClick={() => setOpen(false)} />
        </Stack>
        <div style={{ textAlign: "center", paddingBottom: "12px" }}>
          <GuestBrokerSvg />
        </div>
        <div
          style={{
            background: "#f8f9f9",
            padding: "10px",
            maxWidth: "420px",
            margin: "0px auto",
            marginBottom: "10px",
          }}>
          {guestBrokerDetails}
        </div>
        <div
          style={{
            maxWidth: "370px",
            textAlign: "left",
            margin: "0px auto",
            paddingBottom: "22px",
          }}>
          {benefitLines.map((element, iterator) => {
            return (
              <Stack direction={"row"} spacing={1} mb={1} key={iterator}>
                <CheckCircle sx={{ fontSize: "18px" }} />
                <Typography sx={{ fontSize: "14px" }}>{element}</Typography>
              </Stack>
            );
          })}
          <Typography sx={{ fontSize: "14px" }}>and many more...</Typography>
        </div>
        <div style={{ textAlign: "center", paddingBottom: "20px" }}>
          <StyledButton sx={{ maxWidth: "200px" }} onClick={() => handleVerification()}>
            Verify Account now
          </StyledButton>
        </div>
      </Box>
    </Modal>
  );
};

export const BrokerModal = (props: {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const { open, setOpen } = props;
  const { broker } = useUserSlice();
  const { countryCode, email, phoneNumberAU, phoneNumberUS } = broker;
  const { isGuest, userPermission } = usePermission();
  const userPermissions = userPermission.split(",");
  const isOnline = isGuest ? false : isBrokerOnline(countryCode || "AU");

  const brokerDetails = {
    email,
    phoneNumberAU,
    phoneNumberUS,
    isOnline: isOnline ?? false,
  };

  return (
    <>
      {!userPermission && <p>Loading...</p>}
      {userPermissions.includes("guest") ? (
        <GuestBrokerModal open={open} setOpen={setOpen} />
      ) : (
        <UserBrokerModal open={open} setOpen={setOpen} brokerDetails={brokerDetails} />
      )}
    </>
  );
};

const OnClickWrapper = (props: { children: JSX.Element; data: string }) => {
  const callOrEmail = props.data?.includes("@") ? "Email" : "Call";
  const redirectUrl = "tel:" + props.data;
  return (
    <div
      style={{
        cursor: "pointer",
      }}
      onClick={(e) => {
        if (callOrEmail === "Email") {
          trackEvent(`User_Click_On_Broker_Email_Address`, {
            BrokerEmail: props.data,
          });
        } else {
          trackEvent(`User_Click_On_Broker_Phone_Number`, {
            BrokerPhoneNumber: props.data,
          });
        }

        window.location.href = redirectUrl;
        e.preventDefault();
      }}>
      {props.children}
    </div>
  );
};

export const MobilePhoneEmailICons = () => {
  const { data, isSuccess } = api.useGetUserDatav2Query();
  const { isGuest } = usePermission();
  // US-based users ring the broker's US line; everyone else rings AU
  const brokerPhone =
    (data?.address?.country === "US" ? data?.broker?.phoneNumberUS : data?.broker?.phoneNumberAU) ??
    "";
  if (isGuest) return <div></div>; // User is a guest return nothing

  return (
    <Stack direction={"row"} alignSelf="center">
      {isSuccess && (
        <>
          <CopyOnClick text={data.broker?.email}>
            <Tile icon={<Email sx={{ fontSize: "18px", color: "#FFF" }} />} borderColor={"FFF"} />
          </CopyOnClick>
          <OnClickWrapper data={brokerPhone}>
            <Tile icon={<Phone sx={{ fontSize: "18px", color: "#FFF" }} />} borderColor={"FFF"} />
          </OnClickWrapper>
        </>
      )}
    </Stack>
  );
};
